import { assertNotEmpty, dateUtils, type Optional } from '@festina-life/core'
import { RawSelect } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'

import { useConfigurationContext } from '../../context/configurationContext'
import { useTemplateContext } from '../../context/templateContext'
import { usePreviousTemplateVersionInfo } from '../../context/usePreviousTemplateVersionInfo'
import type { TemplateVersionInfo } from '../../types/templateModel'
import { useAllTemplateVersionInfosForDate } from '../../uiModels/versionInfo/useTemplateVersionInfo'
import { ShadowedVersionTitle } from './ShadowedVersionTitle'

const SELECT_ID = 'diffAgainstVersion'
export const TemplatePreviousVersionDiffSelector = () => {
  const translate = useTranslate()
  const { viewAt } = useConfigurationContext()
  const {
    templateVersion,
    previousTemplateVersion,
    onChangePreviousTemplateVersionOperationKey,
  } = useTemplateContext()
  const previousVersionInfo = usePreviousTemplateVersionInfo(
    templateVersion?.versionInfo,
  )
  const allVersionsForViewDate = useAllTemplateVersionInfosForDate({
    id: templateVersion?.versionInfo.identifier,
    date: viewAt,
  })

  const allVersionsForViewDateExcludingSelf = allVersionsForViewDate
    .filter(
      (versionInfo) =>
        versionInfo.operationKey !== templateVersion?.versionInfo.operationKey,
    )
    .toReversed()

  assertNotEmpty(templateVersion)

  const handleDiffAgainstVersion = (
    versionInfo: Optional<TemplateVersionInfo>,
  ) => {
    if (
      previousTemplateVersion?.versionInfo.operationKey !==
      versionInfo?.operationKey
    ) {
      onChangePreviousTemplateVersionOperationKey(versionInfo?.operationKey)
    }
  }

  return allVersionsForViewDateExcludingSelf.length ? (
    <div className="flex gap-x-2 items-center">
      <label htmlFor={SELECT_ID} className="text-nowrap">
        {translate('templateViewer.previousVersionDiffSelector.label')}
      </label>
      <RawSelect
        id={SELECT_ID}
        options={allVersionsForViewDateExcludingSelf}
        onChange={handleDiffAgainstVersion}
        optionsClassName="w-fit!"
        placeholder={
          previousVersionInfo
            ? translate(
                'templateViewer.previousVersionDiffSelector.emptyOption',
                {
                  fromDate: dateUtils.dateAsStr(previousVersionInfo.fromDate),
                },
              )
            : translate('none')
        }
        renderFn={(versionInfo) => (
          <div>
            <ShadowedVersionTitle
              versionInfo={versionInfo}
              isFirstShadowedVersion={
                allVersionsForViewDateExcludingSelf.at(0) === versionInfo
              }
            />
            <div className="text-sm opacity-60 max-w-md text-warning-500 dark:text-warning-300">
              {versionInfo?.changesetHistory?.description ??
                versionInfo?.operationKey}
            </div>
          </div>
        )}
        renderValueFn={(versionInfo) => (
          <ShadowedVersionTitle
            versionInfo={versionInfo}
            isFirstShadowedVersion={
              allVersionsForViewDateExcludingSelf.at(0) === versionInfo
            }
          />
        )}
        keyFn={(versionInfo) => versionInfo?.operationKey ?? ''}
        value={
          previousTemplateVersion?.versionInfo.operationKey ===
          previousVersionInfo?.operationKey
            ? null
            : previousTemplateVersion?.versionInfo
        }
      />
    </div>
  ) : null
}
